import { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TripPreviewFlightCard,
  TripPreviewHotelCard,
  TripPreviewModel,
  TripPreviewSlotView,
  TripPreviewTransferCard,
} from 'utils/shared/tripResultBuilderPreview';
import { getCurrency } from 'utils/utils';
import { i18nTB } from 'utils/shared/utils';
import Button from 'components/ui/Button/Button';
import TripBuilderGetPdfModal from './TripBuilderGetPdfModal';
import { ProposalData } from 'ts/shared/interfaces/ProposalData';
import TripBuilderShareActions from './TripBuilderShareActions';
import TripBuilderGetPdfStyles from './TripBuilderGetPdfStyles';
import TripBuilderGetPdfContent from './TripBuilderGetPdfContent';
import { getActiveTripPreviewHotelNames } from 'utils/shared/tripBuilderClientSummary';

type TripBuilderClientSelectionProps = {
  model: TripPreviewModel;
  proposal: ProposalData;
  displayDelta?: boolean;
  selectedRoomByHotel: Record<string, number>;
  hotelImageBySlot?: Record<string, string>;
  proposalId: string;
  onSubmitSelection?: () => void;
  isSubmitting?: boolean;
};

type SelectionRow = {
  id: string;
  title: string;
  subtitle?: string;
  priceLabel: string | null;
};

const isRequired = (label?: string) => (label || '').toLowerCase() !== 'optional';

const formatHotelPrice = (priceLabel: string | null) =>
  priceLabel ? priceLabel.replace(/^From\s+/i, '') : null;

const pushFlight = (
  items: SelectionRow[],
  card: TripPreviewFlightCard,
  subtitle: string | undefined,
  scope: string,
) => {
  items.push({
    id: `flight-${scope}-${card.key}`,
    title: card.route,
    subtitle: subtitle || card.scheduleLabel || undefined,
    priceLabel: card.priceLabel,
  });
};

const pushHotel = (
  items: SelectionRow[],
  card: TripPreviewHotelCard,
  city: string,
  selectedRoomByHotel: Record<string, number>,
) => {
  const roomLabels = (card.rooms || []).map((room) => room.roomLabel).filter(Boolean);
  const roomIndex = selectedRoomByHotel[card.key] ?? 0;
  const selectedRoomLabel = roomLabels[roomIndex];
  const roomPrice = card.rooms?.[roomIndex]?.priceLabel;
  const subtitle = [city, selectedRoomLabel || roomLabels.join(', ')].filter(Boolean).join(' · ');
  items.push({
    id: `hotel-${card.key}`,
    title: card.name,
    subtitle: subtitle || undefined,
    priceLabel: roomPrice ?? formatHotelPrice(card.priceLabel),
  });
};

const pushTransfer = (
  items: SelectionRow[],
  card: TripPreviewTransferCard,
  city: string,
) => {
  items.push({
    id: `transfer-${card.key}`,
    title: card.name,
    subtitle: city || card.scheduleLabel || undefined,
    priceLabel: card.priceLabel,
  });
};

const buildSelectionRows = (
  slots: TripPreviewSlotView[],
  selectedRoomByHotel: Record<string, number>,
): SelectionRow[] => {
  const items: SelectionRow[] = [];
  const seenFlightKeys = new Set<string>();

  const pushFlightOnce = (
    card: TripPreviewFlightCard,
    subtitle: string | undefined,
    scope: string,
  ) => {
    const dedupeKey = `${scope}:${card.key}`;
    if (seenFlightKeys.has(dedupeKey)) {
      return;
    }
    seenFlightKeys.add(dedupeKey);
    pushFlight(items, card, subtitle, scope);
  };

  slots.forEach((slot) => {
    if (slot.beforeSection) {
      const flight = slot.beforeSection.flights.find((card) => card.isActive);
      if (flight) {
        pushFlightOnce(flight, slot.beforeSection.sectionTitle, `before-${slot.rootSlotKey}`);
      }
    }

    slot.chainNodes.forEach((node) => {
      if (node.hotelSection) {
        const hotel = node.hotelSection.hotels.find((card) => card.isActive);
        if (hotel) {
          pushHotel(items, hotel, node.city, selectedRoomByHotel);
        }
      }

      if (node.transferSection) {
        const transfer = node.transferSection.transfers.find((card) => card.isActive);
        if (transfer) {
          pushTransfer(items, transfer, node.city);
        }
      }

      node.middleSections.forEach((section) => {
        const flight = section.flights.find((card) => card.isActive);
        if (flight && section.transitionKey) {
          pushFlightOnce(flight, section.sectionTitle, `middle-${section.transitionKey}`);
        }
      });
    });

    if (slot.afterSection) {
      const flight = slot.afterSection.flights.find((card) => card.isActive);
      if (flight) {
        pushFlightOnce(flight, slot.afterSection.sectionTitle, `after-${slot.rootSlotKey}`);
      }
    }
  });

  return items;
};

const countSelectedHotels = (slots: TripPreviewSlotView[]) =>
  slots.reduce(
    (count, slot) =>
      count +
      slot.chainNodes.filter((node) => node.hotelSection?.hotels.some((card) => card.isActive)).length,
    0,
  );

const getAmountFromPriceLabel = (label: string | null) => {
  if (!label) {
    return '0';
  }

  const match = label.match(/([\d][\d,.]*)/);
  return match?.[1] || label;
};

export const buildMandatoryProgress = (slots: TripPreviewSlotView[]) => {
  let total = 0;
  let selected = 0;

  const countSection = (requirementLabel: string, hasSelection: boolean) => {
    if (!isRequired(requirementLabel)) {
      return;
    }
    total += 1;
    if (hasSelection) {
      selected += 1;
    }
  };

  slots.forEach((slot) => {
    if (slot.beforeSection) {
      countSection(
        slot.beforeSection.requirementLabel,
        slot.beforeSection.flights.some((card) => card.isActive),
      );
    }

    slot.chainNodes.forEach((node) => {
      if (node.hotelSection) {
        countSection(
          node.hotelSection.requirementLabel,
          node.hotelSection.hotels.some((card) => card.isActive),
        );
      }
      if (node.transferSection) {
        countSection(
          node.transferSection.requirementLabel,
          node.transferSection.transfers.some((card) => card.isActive),
        );
      }
      node.middleSections.forEach((section) => {
        countSection(section.requirementLabel, section.flights.some((card) => card.isActive));
      });
    });

    if (slot.afterSection) {
      countSection(
        slot.afterSection.requirementLabel,
        slot.afterSection.flights.some((card) => card.isActive),
      );
    }
  });

  const percent = total ? Math.round((selected / total) * 100) : 0;

  return { total, selected, percent };
};

const TripBuilderClientSelection: FC<TripBuilderClientSelectionProps> = ({
  model,
  proposal,
  displayDelta,
  selectedRoomByHotel,
  hotelImageBySlot,
  proposalId,
  onSubmitSelection,
  isSubmitting,
}) => {
  const { t } = useTranslation(undefined, { i18n: i18nTB });
  const pdfContentRef = useRef<HTMLDivElement>(null);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);

  const items = useMemo(
    () => buildSelectionRows(model.slots, selectedRoomByHotel),
    [model.slots, selectedRoomByHotel],
  );

  const progress = useMemo(() => buildMandatoryProgress(model.slots), [model.slots]);

  const selectedHotels = useMemo(() => countSelectedHotels(model.slots), [model.slots]);

  const currencySymbol = useMemo(
    () => getCurrency(model.totalCurrency || 'usd'),
    [model.totalCurrency],
  );

  const totalAmount = useMemo(
    () => getAmountFromPriceLabel(model.totalLabel),
    [model.totalLabel],
  );

  const isComplete = progress.percent === 100;

  return (
    <aside className="tb-client-selection">
      <div className="tb-client-selection-header">
        <h2 className="tb-client-selection-title">{t('your_selection')}</h2>
      </div>

      <div className="tb-client-selection-list-wrap">
        {!items.length ? (
          <div className="tb-client-selection-empty flex-center flex-column text-center">
            <span className="tb-client-selection-empty-icon text-lg" aria-hidden>
              🏨
            </span>
            <div className="tb-client-selection-empty-title text-bold text-dark">
              {t('no_hotels_selected')}
            </div>
            <div className="tb-client-selection-empty-hint text-light text-sm">
              {t('choose_from_above')}
            </div>
          </div>
        ) : (
          <ul className="tb-client-selection-list">
            {items.map((item) => (
              <li className="tb-client-selection-item" key={item.id}>
                <span className="tb-client-selection-item-dot" aria-hidden />
                <div className="tb-client-selection-item-main">
                  <div className="tb-client-selection-item-title text-bold text-dark">{item.title}</div>
                  {!!item.subtitle && (
                    <div className="tb-client-selection-item-subtitle text-light text-sm">
                      {item.subtitle}
                    </div>
                  )}
                </div>
                {!displayDelta && !!item.priceLabel && (
                  <span className="tb-client-selection-item-price text-bold text-dark" dir="ltr">
                    {item.priceLabel}
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="pr-actions pr-actions-plain w100p tb-client-selection-total">
        <div className="pr-actions-bar w100p flex-column">
          <div className="display-flex w100p flex-between">
            <span className="tb-client-selection-total-title">{t('grand_total')}</span>
            <div className="tb-client-selection-total-amount" dir="ltr">
              <span className="tb-client-selection-total-currency">{currencySymbol}</span>
              <span className="tb-client-selection-total-value">{totalAmount}</span>
            </div>
          </div>
          <p className="tb-client-selection-total-status">
            {selectedHotels
              ? t('hotels_selected_count', { count: selectedHotels })
              : t('no_hotels_selected_short')}
          </p>
        </div>
      </div>

      <div className="pr-actions pr-actions-plain w100p">
        <div className="pr-actions-bar w100p flex-column">
          <TripBuilderShareActions
            proposalId={proposalId}
            pdfContainerRef={pdfContentRef}
            hotelNames={getActiveTripPreviewHotelNames(model)}
            disabled={!isComplete}
            embedded
          />
          <div className="tb-client-selection-actions w100p">
            <Button
              className="btn btn-primary pr-actions-btn"
              title={t('send_to_agent')}
              onClick={() => setIsPdfModalOpen(true)}
              disabled={!isComplete}
            />
          </div>
        </div>
      </div>

      <div ref={pdfContentRef} style={{ display: 'none' }} aria-hidden>
        <TripBuilderGetPdfStyles />
        <TripBuilderGetPdfContent
          model={model}
          proposal={proposal}
          selectedRoomByHotel={selectedRoomByHotel}
          hidePrices={displayDelta}
          hotelImageBySlot={hotelImageBySlot}
        />
      </div>

      {isPdfModalOpen && (
        <TripBuilderGetPdfModal
          close={() => setIsPdfModalOpen(false)}
          proposalId={proposalId}
          proposal={proposal}
          model={model}
          selectedRoomByHotel={selectedRoomByHotel}
          hidePrices={displayDelta}
          hotelImageBySlot={hotelImageBySlot}
        />
      )}
    </aside>
  );
};

export default TripBuilderClientSelection;
